import { Code, Database, Brain, Cloud, Rocket, Users, Network, Terminal, Globe } from 'lucide-react'

const skillCategories = [
  {
    icon: <Code size={32} />,
    name: 'Python',
    color: 'text-primary'
  },
  {
    icon: <Database size={32} />,
    name: 'SQL/BigQuery',
    color: 'text-primary'
  },
  {
    icon: <Brain size={32} />,
    name: 'LLM/NLP',
    color: 'text-primary'
  },
  {
    icon: <Cloud size={32} />,
    name: 'GenAI APIs',
    color: 'text-primary'
  },
  {
    icon: <Rocket size={32} />,
    name: 'Prototyping',
    color: 'text-primary'
  },
  {
    icon: <Users size={32} />,
    name: 'Stakeholder',
    color: 'text-primary'
  },
  {
    icon: <Network size={32} />,
    name: 'ETL/dbt',
    color: 'text-primary'
  },
  {
    icon: <Terminal size={32} />,
    name: 'CI/CD',
    color: 'text-primary'
  }
]

const detailedSkills = [
  {
    category: 'Languages',
    skills: [
      { name: 'Python', level: 95, proficiency: 'Advanced' },
      { name: 'SQL', level: 90, proficiency: 'Expert' },
      { name: 'Bash', level: 75, proficiency: 'Scripting' },
      { name: 'Kotlin', level: 40, proficiency: 'Novice' },
      { name: 'TypeScript', level: 45, proficiency: 'Beginner-Intermediate' }
    ]
  },
  {
    category: 'Data & AI Engineering',
    skills: [
      { name: 'GenAI & Prompt Engineering', level: 95, proficiency: 'Expert' },
      { name: 'OpenAI/OpenRouter APIs', level: 90, proficiency: 'Advanced' },
      { name: 'ETL Pipeline Development', level: 85, proficiency: 'Advanced' },
      { name: 'LLM / SLM / VLM', level: 90, proficiency: 'Advanced' },
      { name: 'BigQuery', level: 80, proficiency: 'Proficient' },
      { name: 'Finetuning & Inferencing', level: 85, proficiency: 'Advanced' }
    ]
  },
  {
    category: 'Cloud & DevOps',
    skills: ['Git', 'CI/CD', 'Docker', 'GCP & AWS', 'Cloudflare']
  }
]

export default function Skills() {
  return (
    <section id="skills" className="section-padding bg-surface-container-low/30">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-headline">
          Technical <span className="text-primary">Skills</span>
        </h2>

        {/* Skills Icon Grid */}
        <div className="bg-surface-container-low rounded-3xl p-8 md:p-12 relative overflow-hidden mb-12">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <Brain size={120} />
          </div>
          <h3 className="text-xs font-bold text-on-surface-variant uppercase tracking-[0.3em] mb-12 flex items-center gap-4">
            Technical Skills & Consulting
            <div className="h-[1px] flex-grow bg-outline-variant/30"></div>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 items-center">
            {skillCategories.map((skill, index) => (
              <div key={index} className="flex flex-col items-center gap-2 group">
                <div className={`${skill.color} group-hover:scale-110 transition-transform`}>
                  {skill.icon}
                </div>
                <span className="text-sm font-headline font-medium text-on-surface-variant group-hover:text-white transition-colors">
                  {skill.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Skills Bars */}
        <div className="space-y-12">
          {detailedSkills.slice(0, 2).map((category, index) => (
            <div key={index} className="bg-surface-container-low p-8 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-6 font-headline uppercase tracking-widest">
                {category.category}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {category.skills.map((skill, skillIndex) => {
                  if (typeof skill === 'string') return null
                  return (
                    <div key={skillIndex} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-on-surface">{skill.name}</span>
                        <span className="text-xs font-bold text-primary px-2 py-0.5 bg-primary/10 rounded">
                          {skill.proficiency}
                        </span>
                      </div>
                      <div className="h-2 w-full bg-surface-container-highest rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-primary-container rounded-full transition-all duration-1000"
                          style={{ width: `${skill.level}%` }}
                        ></div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Cloud & DevOps Tags */}
          <div className="bg-surface-container-low p-8 rounded-xl border-l-4 border-primary">
            <h3 className="text-xl font-bold text-primary mb-6 font-headline uppercase tracking-widest">
              {detailedSkills[2].category}
            </h3>
            <div className="flex flex-wrap gap-3">
              {(detailedSkills[2].skills as string[]).map((skill, index) => (
                <span
                  key={index}
                  className="px-6 py-3 bg-surface-container-highest rounded-xl flex items-center gap-2 text-on-surface font-bold hover:bg-primary/20 transition-all cursor-default"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Linguistic Proficiency */}
        <div className="mt-12 bg-surface-container-lowest p-8 rounded-xl border border-outline-variant/10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-surface-container-high flex items-center justify-center rounded-full">
                <Globe className="text-primary" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-headline font-bold uppercase tracking-widest text-white">Linguistic Proficiency</h3>
                <p className="text-xs text-on-surface-variant uppercase tracking-tighter">Global Communication Ready</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 flex-1 max-w-4xl">
              <div className="border-l border-outline-variant/30 pl-4">
                <span className="text-[10px] text-on-surface-variant uppercase block mb-1">English</span>
                <span className="text-xl font-headline font-bold text-white">Fluent</span>
              </div>
              <div className="border-l border-outline-variant/30 pl-4">
                <span className="text-[10px] text-on-surface-variant uppercase block mb-1">German</span>
                <span className="text-xl font-headline font-bold text-primary">A2 Level</span>
              </div>
              <div className="border-l border-outline-variant/30 pl-4">
                <span className="text-[10px] text-on-surface-variant uppercase block mb-1">Marathi</span>
                <span className="text-xl font-headline font-bold text-white">Native</span>
              </div>
              <div className="border-l border-outline-variant/30 pl-4">
                <span className="text-[10px] text-on-surface-variant uppercase block mb-1">Kannada</span>
                <span className="text-xl font-headline font-bold text-white">Native</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
